import React from 'react';
import {Styled} from './JobApplicationForm.styles';
import {withLocalization} from '@epic-core/localization';
import {Form} from 'react-final-form';
import {Typography, VerticalSpacer} from 'epic-ue-ui';
import EducationFormFieldsBlock from './formSections/EducationFormFieldsBlock';
import {
    globalFormValidation,
    submitJobApplication,
    translateValidationError
} from './jobForm.utils';
import DemographicQuestionsBlock from './formSections/DemographicQuestionsBlock';
import FormSubmitResetButtons from './formElements/FormSubmitResetButtons';
import QuestionsBlock from './formSections/QuestionsBlock';
import {useLocation} from 'react-router-dom';

export interface JobApplicationFormProps {
    getMessage: (key: string) => string;
    jobId: string;
    questions?: {
        required: boolean;
        label: string;
        description: string;
        fields: {
            name: string;
            type: string;
            values: {
                value: number;
                label: string;
            }[];
        }[];
    }[];
    demographicQuestions?: any;
    submitButtonlabel?: string;
    resetButtonlabel?: string;
    education?: string;
    uploadFileLabel?: string;
    enterManuallyButtonlabel?: string;
    pleaseSelectLabel?: string;
    validationErrorMsg?: string;
    submitMessage?: string;
    setSubmitMessage: (s: string) => void;
    setShowJobApplicationForm: (b: boolean) => void;
    sectionRequiredLabel?: string;
    uploadFileValidationLabel?: string;
    jobType?: string;
}

const JobApplicationFormComponent = ({
    getMessage,
    jobId,
    questions,
    demographicQuestions,
    submitButtonlabel,
    resetButtonlabel,
    education,
    enterManuallyButtonlabel,
    pleaseSelectLabel = getMessage('epic.jobs.form.select'),
    validationErrorMsg = getMessage('epic.jobs.form.validationError'),
    submitMessage,
    setSubmitMessage,
    setShowJobApplicationForm,
    sectionRequiredLabel = getMessage('epic.jobs.form.sectionRequired'),
    uploadFileValidationLabel = getMessage('epic.jobs.form.uploadFileValidation'),
    jobType
}: JobApplicationFormProps): JSX.Element => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const gh_src = searchParams.get('gh_src');

    const [submitErrorMsg, setSubmitErrorMsg] = React.useState('');

    // Wrapper to translate error message keys
    const setLocalizedSubmitErrorMsg = (msg: string) => {
        if (msg && msg.startsWith('epic.jobs.form.validation.')) {
            const baseKey = msg.split(':')[0]; // Handle cases with additional error details
            const translatedMsg = getMessage(baseKey);
            const additionalInfo = msg.includes(':') ? msg.substring(msg.indexOf(':')) : '';
            setSubmitErrorMsg(translatedMsg + additionalInfo);
        } else {
            setSubmitErrorMsg(msg);
        }
    };

    return (
        <Styled className="job-application-form">
            <Form
                onSubmit={(values) =>
                    submitJobApplication(
                        jobId,
                        values,
                        setSubmitMessage,
                        setShowJobApplicationForm,
                        setLocalizedSubmitErrorMsg
                    )
                }
                initialValues={{mapped_url_token: gh_src || ''}}
                validate={(values) =>
                    translateValidationError(globalFormValidation(values), getMessage)
                }
                render={({
                    handleSubmit,
                    hasValidationErrors,
                    submitFailed,
                    form,
                    submitSucceeded,
                    values,
                    submitting
                }) => (
                    <>
                        {!(submitSucceeded && submitMessage) && (
                            <>
                                <Typography variant="h2">
                                    {getMessage('epic.jobs.form.applyTitle')}
                                </Typography>
                                <VerticalSpacer variant="heading" />
                                <form onSubmit={handleSubmit}>
                                    <hr />

                                    <QuestionsBlock
                                        questions={questions}
                                        formRenderProps={{
                                            handleSubmit,
                                            hasValidationErrors,
                                            submitFailed,
                                            form,
                                            submitSucceeded,
                                            values
                                        }}
                                        sectionRequiredLabel={sectionRequiredLabel}
                                        isInputsDisabled={
                                            submitSucceeded && submitMessage ? true : false
                                        }
                                        enterManuallyButtonlabel={enterManuallyButtonlabel}
                                        uploadFileValidationLabel={uploadFileValidationLabel}
                                    />

                                    <hr />

                                    {education && (
                                        <>
                                            <EducationFormFieldsBlock
                                                form={form}
                                                jobId={jobId}
                                                jobType={jobType}
                                                educationRequired={
                                                    education === 'education_required'
                                                }
                                                pleaseSelectLabel={pleaseSelectLabel}
                                                isDisabled={!!(submitSucceeded && submitMessage)}
                                                sectionRequiredLabel={sectionRequiredLabel}
                                            />
                                            <hr />
                                        </>
                                    )}

                                    <VerticalSpacer variant="eyebrow" />

                                    <DemographicQuestionsBlock
                                        demographicQuestions={demographicQuestions}
                                        formRenderProps={{
                                            handleSubmit,
                                            hasValidationErrors,
                                            submitFailed,
                                            form,
                                            submitSucceeded,
                                            values
                                        }}
                                        sectionRequiredLabel={sectionRequiredLabel}
                                        isInputsDisabled={
                                            submitSucceeded && submitMessage ? true : false
                                        }
                                    />

                                    {!(submitSucceeded && submitMessage) && (
                                        <FormSubmitResetButtons
                                            formRenderProps={{
                                                handleSubmit,
                                                hasValidationErrors,
                                                submitFailed,
                                                form,
                                                submitSucceeded,
                                                values
                                            }}
                                            setSubmitErrorMsg={setLocalizedSubmitErrorMsg}
                                            submitButtonlabel={submitButtonlabel}
                                            resetButtonlabel={resetButtonlabel}
                                            isInputsDisabled={submitting}
                                        />
                                    )}

                                    {!submitSucceeded && submitFailed && hasValidationErrors && (
                                        <Typography variant="p" className="validate-submit-msg">
                                            {validationErrorMsg}
                                        </Typography>
                                    )}
                                    {submitFailed && submitErrorMsg && (
                                        <Typography variant="p" className="validate-submit-msg">
                                            {submitErrorMsg}
                                        </Typography>
                                    )}
                                </form>
                            </>
                        )}
                    </>
                )}
            />
        </Styled>
    );
};

const JobApplicationForm = withLocalization(JobApplicationFormComponent);
export default JobApplicationForm;
